import { motion } from "framer-motion";
import {
  INSTRUCTOR,
  INSTRUCTOR_IMG,
  INSTRUCTOR_PORTRAIT,
  INSTRUCTOR_PANEL,
} from "@/lib/data";
import { fadeUp, inViewOnce, easeOutExpo } from "@/lib/motion";

export default function Instructor() {
  return (
    <section
      id="instructor"
      data-testid="instructor-section"
      className="relative py-24 md:py-40 border-t border-[color:var(--line)]"
    >
      <div className="mx-auto max-w-[1360px] px-5 md:px-8">
        <div className="flex items-center justify-between mb-14 md:mb-20">
          <span className="font-mono text-[11px] uppercase tracking-[0.22em] text-[color:var(--ink-3)]">
            Ch. 04 — Instructor
          </span>
          <span className="font-mono text-[11px] tabular-nums text-[color:var(--ink-3)]">
            §004 / 010
          </span>
        </div>

        <div className="grid grid-cols-12 gap-8 md:gap-12 items-start">
          {/* Image cluster */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={inViewOnce}
            transition={{ duration: 1.1, ease: easeOutExpo }}
            className="col-span-12 md:col-span-5 lg:col-span-5 relative"
          >
            {/* Main on-stage portrait */}
            <div className="relative aspect-[4/5] w-full overflow-hidden bg-[color:var(--bone-2)] border border-[color:var(--line-strong)]">
              <img
                src={INSTRUCTOR_PORTRAIT}
                alt={`${INSTRUCTOR.name} presenting at QuantMinds International`}
                className="w-full h-full object-cover object-top"
                loading="lazy"
              />
              <div className="absolute left-0 bottom-0 w-full h-1 bg-[color:var(--copper)]" />
              <div className="absolute left-3 top-3 font-mono text-[9px] uppercase tracking-[0.24em] text-white/90 mix-blend-difference">
                Live · Keynote
              </div>
            </div>

            {/* Close-up inset overlapping */}
            <motion.div
              initial={{ opacity: 0, scale: 0.92 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={inViewOnce}
              transition={{ duration: 0.9, ease: easeOutExpo, delay: 0.25 }}
              className="absolute -bottom-8 -right-4 sm:-right-6 w-[42%] max-w-[190px] aspect-square overflow-hidden border-4 border-[color:var(--bone)] shadow-[0_18px_50px_rgba(15,21,18,0.25)]"
            >
              <img
                src={INSTRUCTOR_IMG}
                alt={`${INSTRUCTOR.name} in conversation`}
                className="w-full h-full object-cover object-center"
                loading="lazy"
              />
            </motion.div>
          </motion.div>

          {/* Copy */}
          <motion.div
            initial="hidden"
            whileInView="show"
            viewport={inViewOnce}
            variants={{
              hidden: {},
              show: { transition: { staggerChildren: 0.08, delayChildren: 0.1 } },
            }}
            className="col-span-12 md:col-span-7 lg:col-span-7 md:pl-4"
          >
            <motion.p
              variants={fadeUp}
              className="font-mono text-[11px] uppercase tracking-[0.24em] text-[color:var(--copper-2)]"
            >
              Learn with
            </motion.p>
            <motion.h2
              variants={fadeUp}
              data-testid="instructor-name"
              className="mt-3 font-serif text-[48px] sm:text-[68px] md:text-[80px] leading-[0.98] tracking-[-0.03em] font-medium text-[color:var(--ink)]"
            >
              Nicole <em className="italic font-normal">Königstein.</em>
            </motion.h2>
            <motion.p
              variants={fadeUp}
              className="mt-3 font-mono text-[12px] uppercase tracking-[0.2em] text-[color:var(--ink-2)]"
            >
              {INSTRUCTOR.role}
            </motion.p>

            {/* Speaking credential chip */}
            <motion.div
              variants={fadeUp}
              className="mt-5 inline-flex items-center gap-2 border border-[color:var(--line-strong)] rounded-full px-3.5 py-1.5"
              data-testid="instructor-credential"
            >
              <span className="w-1.5 h-1.5 rounded-full bg-[color:var(--copper)]" />
              <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-[color:var(--ink-2)]">
                Speaker · {INSTRUCTOR.speakingAt}
              </span>
            </motion.div>

            <motion.p
              variants={fadeUp}
              className="mt-8 max-w-[640px] text-[16px] md:text-[17px] leading-[1.65] text-[color:var(--ink-2)] text-pretty"
            >
              {INSTRUCTOR.bio}
            </motion.p>

            <motion.div
              variants={fadeUp}
              className="mt-8 max-w-[640px] border-l-2 border-[color:var(--copper)] pl-5 py-2"
            >
              <p
                data-testid="instructor-vip-highlight"
                className="font-serif italic text-[18px] md:text-[20px] leading-[1.4] text-[color:var(--ink)]"
              >
                “VIP participants also receive a private 30-minute one-to-one
                session with Nicole.”
              </p>
            </motion.div>

            <motion.a
              variants={fadeUp}
              href="#pricing"
              data-testid="instructor-cta"
              className="mt-10 inline-flex items-center gap-2 font-mono text-[12px] uppercase tracking-[0.2em] text-[color:var(--ink)] border-b border-[color:var(--ink)] pb-1 hover:text-[color:var(--copper-2)] hover:border-[color:var(--copper-2)] transition-colors"
            >
              Reserve a VIP Pass →
            </motion.a>
          </motion.div>
        </div>

        {/* Cinematic panel band */}
        <motion.figure
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={inViewOnce}
          transition={{ duration: 1, ease: easeOutExpo }}
          className="mt-20 md:mt-28 relative overflow-hidden border border-[color:var(--line-strong)]"
          data-testid="instructor-panel-image"
        >
          <div className="relative aspect-[16/9] md:aspect-[21/9] w-full">
            <img
              src={INSTRUCTOR_PANEL}
              alt={`${INSTRUCTOR.name} on the AI in finance panel at QuantMinds International`}
              className="w-full h-full object-cover object-center"
              loading="lazy"
            />
            <div
              aria-hidden="true"
              className="absolute inset-0"
              style={{
                background:
                  "linear-gradient(to top, rgba(15,21,18,0.72) 0%, rgba(15,21,18,0.05) 45%)",
              }}
            />
            <figcaption className="absolute left-4 md:left-8 bottom-4 md:bottom-6 right-4 flex items-end justify-between gap-4">
              <div>
                <p className="font-mono text-[9px] md:text-[10px] uppercase tracking-[0.24em] text-[color:var(--copper)]">
                  On stage · QuantMinds International
                </p>
                <p className="mt-1.5 font-serif text-[16px] md:text-[22px] leading-tight text-white max-w-[640px]">
                  Nicole joins the AI-in-finance panel with leading quantitative
                  researchers.
                </p>
              </div>
              <span className="hidden md:block font-mono text-[10px] tabular-nums text-white/70 shrink-0">
                #QUANTMINDS
              </span>
            </figcaption>
          </div>
        </motion.figure>
      </div>
    </section>
  );
}
